import styled from "@emotion/styled";
import { RootLayout } from "./RootLayout";
import {
  RADAR_WEBSITE_URL,
  SIDEKICK_WEBSITE_URL,
} from "@shared/utils/constants";
import { SubscriptionSupportButton } from "@shared/components/SubscriptionSupportButton";
import { useAppLogoAsset } from "@shared/hooks/useAppLogoAsset";

const PageContainer = styled.div<{ $fillViewport: boolean }>`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  background-color: ${(p) => p.theme.colors.background2};

  ${(p) =>
    p.$fillViewport &&
    `
    min-height: 100vh;
    @supports (height: 100dvh) {
      min-height: 100dvh;
    }
  `}

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    align-content: start;
  }

  &[aria-disabled="true"] {
    pointer-events: none;
    * {
      pointer-events: none;
    }
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 2.5rem;

  ${(p) => p.theme.media.mobileDown} {
    margin-bottom: 1.5rem;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 4rem 8rem 3rem 3rem;
  background-color: ${(p) => p.theme.colors.primary};

  ${(p) => p.theme.media.desktopDown} {
    padding: 4rem 6rem 3rem 2rem;
  }

  ${(p) => p.theme.media.laptopDown} {
    padding: 4rem 2rem 3rem 2rem;
  }

  ${(p) => p.theme.media.mobileDown} {
    padding: 3rem 1.5rem 3rem;
  }

  > div {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 600px;
    height: 100%;

    ${(p) => p.theme.media.mobileDown} {
      max-width: 100%;
      height: auto;
    }
  }
`;

const RightContainer = styled.div`
  width: 100%;
  padding: 9rem 3rem 3rem 8rem;
  position: relative;
  overflow-y: auto;
  background-color: ${(p) => p.theme.colors.background2};

  ${(p) => p.theme.media.desktopDown} {
    padding: 9rem 3rem 3rem 6rem;
  }

  ${(p) => p.theme.media.laptopDown} {
    padding: 9rem 2rem 5rem 2rem;
  }

  ${(p) => p.theme.media.mobileDown} {
    padding: 0 0.5rem 3rem;
    overflow-y: initial;
  }

  h1 {
    margin: 0;
    margin-bottom: 1rem;
    color: ${(p) => p.theme.text.colors.primary};
  }

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const RightContentWrapper = styled.div<{ maxWidth: string }>`
  width: 100%;
  max-width: ${(p) => p.maxWidth};

  ${(p) => p.theme.media.mobileDown} {
    max-width: 100%;
  }
`;

type Props = {
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  rightMaxWidth?: string;
  theme?: "dark" | "light";
  disabled?: boolean;
  topActions?: React.ReactNode;
  hideSupportButton?: boolean;
  fillViewport?: boolean;
};

export function SubscriptionPageLayout(props: Props) {
  const {
    leftContent,
    rightContent,
    rightMaxWidth = "600px",
    theme,
    disabled,
    hideSupportButton,
    fillViewport = true,
  } = props;
  const {
    src: logoSrc,
    alt: logoAlt,
    height: logoHeight,
    isRadarDomain,
  } = useAppLogoAsset("white", { sidekick: "md", radar: "lg" });
  const websiteUrl = isRadarDomain ? RADAR_WEBSITE_URL : SIDEKICK_WEBSITE_URL;

  const content = (
    <PageContainer aria-disabled={disabled} $fillViewport={fillViewport}>
      {!hideSupportButton && <SubscriptionSupportButton />}
      <LeftContainer>
        <div>
          <LogoContainer>
            <a href={websiteUrl}>
              <img src={logoSrc} alt={logoAlt} style={{ height: logoHeight }} />
            </a>
          </LogoContainer>
          {leftContent}
        </div>
      </LeftContainer>
      <RightContainer>
        <RightContentWrapper maxWidth={rightMaxWidth}>
          {rightContent}
        </RightContentWrapper>
      </RightContainer>
    </PageContainer>
  );

  return <RootLayout theme={theme}>{content}</RootLayout>;
}
